<script setup lang="ts">
import { Cart } from '@/@types/cart'
import Resume from '@/components/shared/cart/Resume.vue'
import { CartComponent } from '@/components/shared/cart/steps'
import { Card } from '@/components/ui/card'
import { Stepper, StepperIndicator, StepperItem, StepperTitle } from '@/components/ui/stepper'
import { Title } from '@/components/ui/title'
import { cartTotalsKeys, useCartTotals } from '@/composables/useCartTotals'
import LightLayout from '@/layouts/LightLayout.vue'
import { Head } from '@inertiajs/vue3'
import { Check } from 'lucide-vue-next'
import { computed, provide, ref } from 'vue'

const { cart } = defineProps<{
    cart: Cart
}>()

const currentStep = ref(1)

const agreed = ref(false)

const sessionCart = ref(true)

provide('stepControl', {
    currentStep,
    agreed,
    sessionCart,
})

const steps = [
    { step: 1, title: 'Seus ingressos' },
    { step: 2, title: 'Forma de pagamento' },
    { step: 3, title: 'Dados de faturamento' },
    { step: 4, title: 'Resumo da compra' },
]

const lastStep = steps.length

const cartItems = computed(() => cart.items)

const cartTotals = useCartTotals(cartItems)

provide(cartTotalsKeys, cartTotals)
</script>

<template>
    <Head :title="$t('cart.title')"></Head>

    <LightLayout :hideFooter="true">
        <div v-if="!cart.items.length" class="container">
            <Card class="p-8">
                {{ $t('Your cart is empty') }}
            </Card>
        </div>
        <div v-else class="relative container flex flex-col gap-10 lg:flex-row">
            <div class="flex flex-1 flex-col gap-8">
                <Title class="!text-3xl">{{ currentStep === lastStep ? $t('Cart') : $t('Finalize purchase') }}</Title>
                <Stepper v-model="currentStep" class="font-inter flex w-full flex-col items-start gap-2 overflow-x-auto lg:flex-row">
                    <StepperItem v-for="step in steps" :key="step.step" v-slot="{ state }" class="flex w-fit lg:px-2" :step="step.step">
                        <StepperIndicator
                            class="group-data-[state=completed]:bg-primary group-data-[state=active]:bg-tranparent group-data-[state=active]:border-primary group-data-[state=active]:text-primary h-[40px] w-[40px] group-data-[state=active]:border group-data-[state=active]:border-dashed group-data-[state=completed]:text-white group-data-[state=inactive]:border"
                        >
                            <Check v-if="state === 'completed'" />
                            <span v-if="state != 'completed'">{{ String(step.step).padStart(2, '0') }}</span>
                        </StepperIndicator>
                        <StepperTitle
                            class="group-data-[state=active]:text-primary text-sm font-medium text-gray-900 group-data-[state=inactive]:text-slate-400"
                        >
                            {{ step.title }}
                        </StepperTitle>
                    </StepperItem>
                </Stepper>
                <CartComponent :cart="cart" />
            </div>
            <form @submit.prevent="$inertia.visit('/session_cart/require_login')" id="form"></form>
            <Resume :cartItems="cart.items" />
        </div>
    </LightLayout>
</template>
